import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useLabels, useRequestData, usePrefs } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import { isBrowser } from 'ws-scripts/modules/environment';
import {
	HighlightedAttribute,
	isValidFuelType,
	getFuelTypeNote,
	isInTransit,
	getInTransitLabel,
	highlightedAttrList,
	getVehicleStatusNote,
	getCityStateNote,
	QuickNotesButton
} from 'wsm-highlighted-attribute';

import { VehicleViews } from 'wsm-vehicle-views';
import { ErrorBoundary } from 'wsm-error-boundary';
import {
	getVehicleTitle,
	isExistAttrConfig,
	remapPrefs,
	isVehicleMakeMatchAccountFranchise
} from '../utils/utility';
import { highlightedAttrs } from '../global-constants';
import { sortQuickNotes } from '../utils/sortQuickNotes';

const highlightedAttributeFactory = ({
	type = '',
	content = '',
	badgeClasses,
	isVehicleView = false
}) => {
	return (
		<>
			{isVehicleView ? (
				content
			) : (
				<ErrorBoundary newRelicPageAction="ws-vehicle-title HighlightedAttribute error boundary">
					<HighlightedAttribute
						type={type}
						badgeClasses={badgeClasses}
					>
						{content}
					</HighlightedAttribute>
				</ErrorBoundary>
			)}
		</>
	);
};

const VehicleTitle = ({ isInSidebar = false, hasViewsData, numberOfViews }) => {
	const labels = useLabels();

	const { deviceType, locale } = useRequestData();
	const unmappedPrefs = usePrefs();

	// Memoize the expensive prefs remapping operation
	const prefs = useMemo(() => {
		return isInSidebar
			? remapPrefs(unmappedPrefs, '-sidebar')
			: unmappedPrefs;
	}, [unmappedPrefs, isInSidebar]);

	const { forceShowVehicleCondition } = prefs;
	// Combine selectors to prevent multiple store subscriptions
	const storeData = useSelector((state) => ({
		title: state.title,
		vehicle: state.vehicle,
		highlightedAttrsConfig: state.highlightedAttrsConfig
	}));

	const { title, vehicle, highlightedAttrsConfig } = storeData;

	const displaySeoSubTitle = vehicle.seoSubTitle.length;
	const SeoSubTitleLine = displaySeoSubTitle ? (
		<span
			className={setClassNames([
				'additional-details',
				'd-block',
				'mt-0',
				'mb-1',
				'font-weight-normal',
				prefs.titleDetailsSize,
				prefs.titleDetailsColor
			])}
		>
			{vehicle.seoSubTitle}
		</span>
	) : null;

	const {
		estimatedDeliveryDate,
		status,
		certified,
		type,
		classification,
		vehicleAccountName,
		address
	} = vehicle;

	// condition for showing highlight arttributes
	const shouldShowHighlightedInTransit =
		isInTransit(status) &&
		isExistAttrConfig(
			highlightedAttrsConfig,
			highlightedAttrs.ESTIMATED_DELIVERY_DATE
		);

	const shouldShowConditionAsHighlightedAttribute =
		forceShowVehicleCondition === 'false' &&
		isExistAttrConfig(highlightedAttrsConfig, highlightedAttrs.CONDITION);

	const shouldShowOnsiteVehicle =
		!shouldShowHighlightedInTransit &&
		vehicle.isOffSiteVehicle === false &&
		isExistAttrConfig(
			highlightedAttrsConfig,
			highlightedAttrs.ON_SITE_VEHICLE
		);

	const shouldShowSharedVehicle =
		!shouldShowHighlightedInTransit &&
		vehicle.isOffSiteVehicle === true &&
		isExistAttrConfig(
			highlightedAttrsConfig,
			highlightedAttrs.SHARED_VEHICLE
		);

	const shouldShowHighlightedFuelType =
		vehicle.normalFuelType &&
		isValidFuelType(vehicle.normalFuelType) &&
		isExistAttrConfig(highlightedAttrsConfig, highlightedAttrs.FUEL_TYPE);

	const shouldShowVehicleClassification =
		isExistAttrConfig(
			highlightedAttrsConfig,
			highlightedAttrs.CLASSIFICATION
		) &&
		!!vehicle?.classification &&
		vehicle?.classification?.toLowerCase() !== 'primary';

	const shouldShowVehicleViews =
		hasViewsData &&
		numberOfViews > 0 &&
		numberOfViews >= prefs.vehicleViews_minimumViews;

	const shouldShowCityState = isExistAttrConfig(
		highlightedAttrsConfig,
		highlightedAttrs.CITY_STATE
	);

	const quickNotesAttrs = [];

	// If shown in-transit, the screen will not show on-site and transfer-available
	let vehicleOnSiteAttribute;
	if (shouldShowOnsiteVehicle) {
		const vehicleOnSiteText = labels.get('ON_SITE_HIGHLIGHTED_ATTRIBUTE');
		vehicleOnSiteAttribute = highlightedAttributeFactory({
			type: 'onSiteVehicle',
			content: vehicleOnSiteText,
			badgeClasses: ['mr-2', 'in-stock', 'mb-2']
		});

		quickNotesAttrs.push({
			type: 'onSiteVehicle',
			highlightedAttr: vehicleOnSiteAttribute,
			note: getVehicleStatusNote(
				highlightedAttrList.ONSITE,
				vehicleAccountName,
				labels
			)
		});
	}

	let inTransitAttribute;
	if (shouldShowHighlightedInTransit) {
		const inTransitText = getInTransitLabel({
			estimatedDeliveryDate,
			locale,
			labels
		});
		inTransitAttribute = highlightedAttributeFactory({
			type: 'inTransit',
			content: inTransitText,
			badgeClasses: [
				'default',
				'mr-2',
				shouldShowHighlightedFuelType ? 'mb-2' : ''
			]
		});

		quickNotesAttrs.push({
			type: 'inTransit',
			highlightedAttr: inTransitAttribute,
			note: getVehicleStatusNote(
				highlightedAttrList.ESTIMATED_DELIVERY_DATE,
				vehicleAccountName,
				labels
			)
		});
	}

	let vehicleConditionAttribute;
	if (shouldShowConditionAsHighlightedAttribute) {
		let vehicleConditionText;
		let noteLable;
		if (certified && type === 'used') {
			vehicleConditionText = isVehicleMakeMatchAccountFranchise(
				highlightedAttrsConfig,
				vehicle.make
			)
				? labels
						.get('MAKE_CERTIFIED')
						.replace(/\{\{make\}\}/, vehicle.make)
				: labels.get('CERTIFIED_VEHICLE');
			noteLable = labels.get('HIGHLIGHTED_ATTR_CERTIFIED_EXPLAIN');
		} else if (type === 'new') {
			vehicleConditionText = labels.get('NEW');
			noteLable = labels.get('HIGHLIGHTED_ATTR_NEW_EXPLAIN');
		} else if (type === 'used') {
			vehicleConditionText = labels.get('USED');
			noteLable = labels.get('HIGHLIGHTED_ATTR_USED_EXPLAIN');
		}

		vehicleConditionAttribute = highlightedAttributeFactory({
			type: 'condition',
			content: vehicleConditionText,
			badgeClasses: ['mr-2', 'default', 'mb-2']
		});

		quickNotesAttrs.push({
			type: 'condition',
			highlightedAttr: vehicleConditionAttribute,
			note: noteLable
		});
	}

	let vehicleSharedVehicleAttribute;
	if (shouldShowSharedVehicle) {
		const vehicleSharedVehicleText = labels.get(
			'OFF_SITE_HIGHLIGHTED_ATTRIBUTE'
		);
		vehicleSharedVehicleAttribute = highlightedAttributeFactory({
			type: 'sharedVehicle',
			content: vehicleSharedVehicleText,
			badgeClasses: ['mr-2', 'default', 'mb-2']
		});

		quickNotesAttrs.push({
			type: 'sharedVehicle',
			highlightedAttr: vehicleSharedVehicleAttribute,
			note: getVehicleStatusNote(
				highlightedAttrList.OFFSITE,
				vehicleAccountName,
				labels
			)
		});
	}

	let fuelTypePopoverAttribute;
	if (shouldShowHighlightedFuelType) {
		const fuelTypeNoteLabel = getFuelTypeNote({
			fuelType: vehicle.normalFuelType,
			labels
		});

		fuelTypePopoverAttribute = highlightedAttributeFactory({
			type: 'fuelType',
			content: vehicle.normalFuelType,
			badgeClasses: [
				'default',
				'mr-2',
				shouldShowVehicleViews ? 'mb-2' : ''
			]
		});

		quickNotesAttrs.push({
			type: 'fuelType',
			highlightedAttr: fuelTypePopoverAttribute,
			note: fuelTypeNoteLabel || ''
		});
	}

	let vehicleClassificationAttribute;
	if (shouldShowVehicleClassification) {
		const vehicleClassificationAttributeText = labels.get(
			classification.toUpperCase()
		);

		vehicleClassificationAttribute = vehicleClassificationAttributeText
			? highlightedAttributeFactory({
					type: 'classificationName',
					content: vehicleClassificationAttributeText,
					badgeClasses: ['default', 'mr-2', 'mb-2']
			  })
			: null;

		quickNotesAttrs.push({
			type: 'classificationName',
			highlightedAttr: vehicleClassificationAttribute,
			note:
				classification && type
					? labels.get(
							`HIGHLIGHTED_ATTR_${classification.toUpperCase()}_${type.toUpperCase()}_EXPLAIN`
					  )
					: ''
		});
	}

	let vehicleCityStateAttribute;
	if (shouldShowCityState && address) {
		const { city, state } = address;
		const cityState = city && state ? `${city}, ${state}` : '';

		vehicleCityStateAttribute = highlightedAttributeFactory({
			type: highlightedAttrList.CITY_STATE,
			content: cityState,
			badgeClasses: ['default', 'mr-2', 'mb-2']
		});

		quickNotesAttrs.push({
			type: highlightedAttrList.CITY_STATE,
			highlightedAttr: vehicleCityStateAttribute,
			note: getCityStateNote(cityState, labels, { address })
		});
	}

	let vehicleViewsAttribute;
	if (shouldShowVehicleViews) {
		const numberOfDays = Number(
			prefs?.vehicleViews_timeFrame?.replace(/[^0-9]/g, '')
		);
		const daysAlias = numberOfDays && numberOfDays === 1 ? 'DAY' : 'DAYS';
		const vehicleViewsAttributeRender = (
			<ErrorBoundary newRelicPageAction="ws-vehicle-title VehicleViews error boundary">
				<VehicleViews
					vehicleViews={numberOfViews}
					displayStyle={prefs.vehicleViews_displayStyle}
					minimumViews={prefs.vehicleViews_minimumViews}
					timeFrame={prefs.vehicleViews_timeFrame}
				/>
			</ErrorBoundary>
		);

		vehicleViewsAttribute = highlightedAttributeFactory({
			type: 'vehicleViews',
			content: vehicleViewsAttributeRender,
			isVehicleView: true
		});

		quickNotesAttrs.push({
			type: 'vehicleViews',
			highlightedAttr: vehicleViewsAttribute,
			note: `${numberOfViews} ${labels.get(
				'HOTCAR_VIEWS_IN_THE_PAST'
			)} ${numberOfDays} ${labels.get(daysAlias)}`
		});
	}

	const hasAdditionalDetails =
		shouldShowHighlightedFuelType ||
		shouldShowHighlightedInTransit ||
		shouldShowOnsiteVehicle ||
		shouldShowConditionAsHighlightedAttribute ||
		shouldShowSharedVehicle ||
		shouldShowVehicleViews ||
		shouldShowCityState ||
		shouldShowVehicleClassification;

	/* The order in which the highlighted attributes will be rendered inside of the vehicle card.
	Any changes made to the order here will also be updated in the 'Quick Notes' modal. */
	const highlightedAttrsRenderOrder = [
		vehicleOnSiteAttribute,
		inTransitAttribute,
		vehicleConditionAttribute,
		vehicleSharedVehicleAttribute,
		fuelTypePopoverAttribute,
		vehicleClassificationAttribute,
		vehicleCityStateAttribute,
		vehicleViewsAttribute /* Keep vehicle views last to prevent layout shift when data loads asynchronously. */
	];

	const highlightedAttrsRenderOrderValid = highlightedAttrsRenderOrder.filter(
		(attr) => attr
	);

	const additionalDetails =
		hasAdditionalDetails && highlightedAttrsRenderOrderValid?.length > 0 ? (
			<QuickNotesButton
				quickNotesAttrs={sortQuickNotes(
					highlightedAttrsRenderOrder,
					quickNotesAttrs
				)}
			>
				<ul
					className={setClassNames([
						'additional-details',
						'list-inline',
						'mb-0',
						'font-weight-normal',
						'd-flex',
						'flex-wrap',
						'ml-0',
						prefs.titleDetailsSize,
						'mt-3',
						prefs.titleDetailsColor
					])}
				>
					{highlightedAttrsRenderOrderValid.map((attribute) => (
						<li className="px-0" key={attribute.props.type}>
							{attribute}
						</li>
					))}
				</ul>
			</QuickNotesButton>
		) : null;
	const vehicleTitle = getVehicleTitle(
		title,
		labels,
		vehicle.type,
		vehicle.certified,
		prefs.forceShowVehicleCondition,
		prefs.showVehicleTitleSeparators,
		prefs.vehicleTitleSeparator
	);
	const pubsub = isBrowser && window?.DDC?.pubsub;
	useEffect(() => {
		if (pubsub && vehicleTitle) {
			pubsub?.publishDelayed(
				'ws-vehicle-toolbar/show-vehicle-title',
				vehicleTitle
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vehicleTitle]);

	const decodeMarkup = (encodedTitle) => {
		return { __html: `${encodedTitle}` };
	};

	if (Object.keys(title).length === 0 && title.constructor === Object) {
		return null;
	}

	if (prefs.enableLineBreak === 'false') {
		return (
			<>
				<h1 className={setClassNames(['vehicle-title', 'h2', 'm-0'])}>
					<span
						// eslint-disable-next-line react/no-danger
						dangerouslySetInnerHTML={decodeMarkup(
							vehicleTitle.join(' ')
						)}
					/>
					{SeoSubTitleLine}
				</h1>
				{additionalDetails}
			</>
		);
	}

	return (
		<>
			<h1
				className={setClassNames([
					'vehicle-title',
					'm-0',
					'line-height-reset',
					deviceType !== 'DESKTOP' && 'h2'
				])}
			>
				<span
					className={setClassNames([
						'd-block',
						prefs.titleHeading,
						prefs.titleHeadingColor,
						prefs.titleHeadingWeight,
						prefs.titleHeadingSize
					])}
					// eslint-disable-next-line react/no-danger
					dangerouslySetInnerHTML={decodeMarkup(
						vehicleTitle.length > 1 && vehicleTitle[0]
					)}
				/>
				<span
					className={setClassNames([
						prefs.titleHeadingColor,
						prefs.titleHeadingTagLineWeight,
						prefs.titleHeadingTagLineSize
					])}
					// eslint-disable-next-line react/no-danger
					dangerouslySetInnerHTML={decodeMarkup(
						vehicleTitle.length > 1
							? vehicleTitle[1]
							: vehicleTitle[0]
					)}
				/>
				{SeoSubTitleLine}
			</h1>
			{additionalDetails}
		</>
	);
};

VehicleTitle.propTypes = {
	isInSidebar: PropTypes.bool,
	hasViewsData: PropTypes.bool.isRequired,
	numberOfViews: PropTypes.number.isRequired
};

export default VehicleTitle;
